import { ng } from "@common/ngds";
import { useProject } from "../../features/projects/projectSlice";
import { Drawer, lerp, Svg } from "@common/components/Rendering";
import {
  ArrowId,
  ColumnMarkers,
  coordinate_axis_style,
  coordinate_axis_text_style,
  cs_alignment_dist,
  cs_meassure_dist,
  cs_section_label_style,
  cs_view_dist,
  cs_view_dist_alt,
  view_sel_style,
  view_style,
} from "../ViewportStyles";
import { drawGeomCrossSection, Extent } from "./drawGeomCrossSection";
import { drawHorizontalMeassurment, drawVerticalMeassurment } from "./drawMeassurement";
import {
  drawViewDirection_left_bottom,
  drawViewDirection_left_top,
  drawViewDirection_up_left,
  drawViewDirection_up_right,
} from "./drawViewDirection";
import { drawLongitudalReinforcement } from "./drawLongitudalReinf";
import { useExactBarMarks } from "../../features/projects/useExactBarMarks";
import { drawStirrup, shouldDrawStirrup } from "./drawStirrup";
import { useSelector } from "react-redux";
import {
  selectShowSectionBuckling,
  selectShowSectionDimensions,
  selectShowSectionFireIndicator,
} from "../../features/ui/uiSlice";
import { drawCoordinateSystem } from "./drawCoordinateSystem";
import { useDevConfig } from "../../App";
import { useAutoZoomViewport } from "../../features/user/userSlice";
import {
  dsiplay_detail_meassure_from_meter,
  findSectionInProject,
  geomStyle_from_materialCategory,
  section_material_label,
} from "./shared";
// Experimental: fire exposure overlay rendering for cross-section preview.
import {
  drawCircularFireExposure,
  drawCircularSteelFireProtection,
  drawCircularTimberFireProtectionBorder,
  drawFireExposure,
  FIRE_ICON_OFFSET,
  drawSteelFireIcons,
  drawSteelFireProtection,
  drawTimberFireIcons,
  drawTimberFireProtectionBorder,
  drawTimberStructProtectionSides,
  steelFireExposureSidesFromExposure,
  steelFireProtectionSidesFromExposure,
} from "./drawFireExposure";
import { bucklingLoadPosition, drawLoadPosition } from "./drawLoadPosition";

interface Meassurement {
  hori: number | null;
  vert: number | null;
}

const calculate_meassurment_data = (projectData: ng.frontend.column.Project | null, geom: Extent): Meassurement => {
  const { sec, allSecs, first_element, first_segment, secGuid } = findSectionInProject(projectData);
  const params = sec?.params;
  const pt = params?.type;

  switch (pt) {
    case "rParams":
      return {
        vert: params.rParams.height,
        hori: params.rParams.width,
      };
    case "sParams":
      return {
        hori: params.sParams.side,
        vert: params.sParams.side,
      };
    case "cParams":
      return {
        hori: params.cParams.diameter,
        vert: params.cParams.diameter,
      };
    default:
      if (params === null) {
        // parms is not stored in the database, handle by guessing
        return {
          vert: geom.ver.max - geom.ver.min,
          hori: geom.hor.max - geom.hor.min,
        };
      }
      console.log(`Unhandled param type ${pt}`);
      if (pt === undefined) {
        if (params === null) {
          console.log("warning: params is null for sec ", allSecs);
        } else {
          // print extra stuff to find out why pt is undefined (this shouldn't happen)
          console.log("[CrossSectionView] Section measurement debug", {
            hasProjectData: projectData !== null,
            structureCount: projectData?.data?.input?.structures?.length ?? 0,
            hasFirstElement: first_element !== undefined,
            hasFirstSegment: first_segment !== undefined,
            secGuid,
            secDbCount: projectData?.data?.input?.secDb?.length ?? 0,
            foundSection: sec !== undefined,
            sectionIdGuid: sec?.id?.guid,
            hasParams: params !== undefined,
            paramKeys: params ? Object.keys(params) : [],
            sectionRaw: sec,
            segmentLayerStart: first_segment?.layer?.start,
            params,
            pt,
          });
        }
      }
      return {
        hori: null,
        vert: null,
      };
  }
};

export const CrossSectionView = (props: {
  a_selected: boolean;
  b_selected: boolean;
  select_a: () => void;
  select_b: () => void;
}) => {
  const { project: projectSource } = useProject();
  const exactBarMarks = useExactBarMarks();
  const showDimensionsSetting = useSelector(selectShowSectionDimensions);
  const showFireIndicator = useSelector(selectShowSectionFireIndicator);
  // The fire icons and (for steel/timber) protection lines sit close to the section outline,
  // often overlapping the dimension lines drawn just outside it - so dimensions are hidden here
  // whenever the fire indicator is shown. This only affects the cross-section view; the
  // dimensions checkbox itself (and side/3D views, which don't draw fire indicators) are
  // untouched.
  const showMeassure = showDimensionsSetting && !showFireIndicator;
  // The buckling load position symbol follows the same visibility rule as the buckling shape,
  // i.e. it is on while the Buckling accordion is open (see the effect in App.tsx).
  const showBuckling = useSelector(selectShowSectionBuckling);
  const project = { ...projectSource, data: ng.frontend.column.Project.fromObject(projectSource?.data) };
  const drawer = new Drawer<ArrowId>();
  const isDev = useDevConfig();
  const autoZoom = useAutoZoomViewport();
  // Read fire flags from the raw Redux JSON payload so viewport updates track UI toggles immediately.
  // Concrete fire settings are stored under designSettings.rc.beam.fire (not designSettings.concrete.*).
  const designSettingsJson = projectSource?.data?.data?.input?.structures?.[0]?.elements?.[0]?.designSettings ?? null;
  const designSettings = designSettingsJson ? ng.design.ElementDesignSettings.fromObject(designSettingsJson) : null;
  const fire = designSettings?.rc?.getColumn()?.fire;
  const steelFire = designSettings?.steel?.fire;
  // steelFire is a typed Fire instance - protection_type is a oneof, so use the getter (mirrors
  // SXInputSteelFireSettings). When the material tab is active and "none" (guid "none_" or missing)
  // is selected, no protection is actually applied, so the indicator should not be drawn.
  const steelProtectingMaterial = steelFire?.getProtectingMaterial();
  const isSteelMaterialProtection = steelProtectingMaterial != null;
  const steelProtectionGuid = steelProtectingMaterial?.guid;
  const isSteelProtectionNoneSelected =
    isSteelMaterialProtection && (!steelProtectionGuid || steelProtectionGuid === "none_");
  const timberFire = designSettings?.timber?.fire;
  const timberProtection = timberFire?.protection;
  // Mirrors SXInputTimberFireSettings: default to MATERIAL_TYPE_NONE when no material props are
  // stored yet, and treat both NONE and UNSPECIFIED as "no protecting material selected".
  const timberMtrlType =
    timberProtection?.mtrl?.properties?.materialType ?? ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_NONE;
  const hasTimberProtectingMaterial =
    timberMtrlType !== ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_NONE &&
    timberMtrlType !== ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_UNSPECIFIED;

  const geomResult = drawGeomCrossSection(
    drawer,
    project.data,
    geomStyle_from_materialCategory(findSectionInProject(project.data)?.sec?.materialCategory),
  );
  const geom = geomResult ?? {
    hor: { max: 0, min: 0 },
    ver: { max: 0, min: 0 },
  };

  // Draw fire exposure indicators for concrete sections.
  // Keep defaults aligned with SXInputFireSettings so indicators are visible
  // even before fire settings have been explicitly persisted to the project.
  if (geomResult && showFireIndicator) {
    const section = findSectionInProject(project.data)?.sec;
    const isConcrete = section?.materialCategory === ng.sections.MaterialCategory.MATERIAL_CATEGORY_CONCRETE;
    const isSteel = section?.materialCategory === ng.sections.MaterialCategory.MATERIAL_CATEGORY_STEEL;
    const isTimber = section?.materialCategory === ng.sections.MaterialCategory.MATERIAL_CATEGORY_TIMBER;
    const isCircular = section?.params?.type === "cParams";
    if (isConcrete) {
      // For circular sections upperSide means the entire perimeter (rc-design-api contract, and the
      // fire settings panel only offers that one switch), so draw a ring of flames instead of
      // flames along the four faces of the bounding box.
      if (isCircular) {
        drawCircularFireExposure(drawer, geomResult, fire?.upperSide ?? true);
      } else {
        drawFireExposure(drawer, geomResult, {
          upperSide: fire?.upperSide ?? false,
          bottomSide: fire?.bottomSide ?? true,
          leftSide: fire?.leftSide ?? true,
          rightSide: fire?.rightSide ?? true,
        });
      }
    } else if (isSteel) {
      // Steel uses the exposure dropdown for flame-icon placement, while an applied protecting
      // material still wraps the whole section in the viewport.
      const sectionExposure = steelFire?.sectionExposure ?? ng.design.steel.SectionExposure.SECTION_EXPOSURE_ALL_SIDES;
      if (!isSteelProtectionNoneSelected) {
        if (isCircular) {
          drawCircularSteelFireProtection(drawer, geomResult);
        } else {
          drawSteelFireProtection(
            drawer,
            geomResult,
            steelFireProtectionSidesFromExposure(ng.design.steel.SectionExposure.SECTION_EXPOSURE_ALL_SIDES),
          );
        }
      }
      drawSteelFireIcons(drawer, geomResult, steelFireExposureSidesFromExposure(sectionExposure));
    } else if (isTimber) {
      // Timber draws three independent indicators (see SXInputTimberFireSettings and
      // drawFireExposure.ts): a thin border around the section when a protecting material is
      // selected (skipping faces that already have a "protecting structure" line), a thicker
      // line outside each face where the "protecting structure" switch is enabled, and a flame
      // icon on every face indicating fire always acts on the column.
      const structProtectionSides = {
        upperSide: timberProtection?.structProtectionTop ?? false,
        bottomSide: timberProtection?.structProtectionBottom ?? false,
        leftSide: timberProtection?.structProtectionLeft ?? false,
        rightSide: timberProtection?.structProtectionRight ?? false,
      };
      if (hasTimberProtectingMaterial) {
        if (isCircular) {
          drawCircularTimberFireProtectionBorder(drawer, geomResult);
        } else {
          drawTimberFireProtectionBorder(drawer, geomResult, structProtectionSides);
        }
      }
      if (!isCircular) {
        drawTimberStructProtectionSides(drawer, geomResult, structProtectionSides);
      }
      // Fire acts on every side of the column regardless of the protecting material/structure
      // settings (those only affect how the char depth is calculated), so always draw a flame
      // icon outside each face.
      drawTimberFireIcons(drawer, geomResult);
    }
  }

  // Buckling load position (top edge / center of gravity / bottom edge) - only steel and timber
  // have that dropdown, and only one of the three symbols is ever drawn.
  if (geomResult && showBuckling) {
    drawLoadPosition(drawer, geomResult, bucklingLoadPosition(project.data));
  }

  if (showMeassure) {
    const meassurment = calculate_meassurment_data(project.data, geom);
    if (meassurment.vert) {
      drawVerticalMeassurment(drawer, {
        start_hor: geom.hor.max,
        end_hor: geom.hor.max + cs_meassure_dist,
        start_vert: geom.ver.min,
        end_vert: geom.ver.max,
        alignment: cs_alignment_dist,
        text: dsiplay_detail_meassure_from_meter(meassurment.vert),
      });
    }
    if (meassurment.hori) {
      drawHorizontalMeassurment(drawer, {
        start_hor: geom.hor.min,
        end_hor: geom.hor.max,
        start_vert: geom.ver.max,
        end_vert: geom.ver.max + cs_meassure_dist,
        alignment: cs_alignment_dist,
        text: dsiplay_detail_meassure_from_meter(meassurment.hori),
      });
    }
  }

  const axisSpan = Math.max(geom.hor.max - geom.hor.min, geom.ver.max - geom.ver.min);
  const axisLengthScale = 2 / 3;
  const axisLength = Math.max(0.02, Math.min(0.05, axisSpan * 0.125)) * axisLengthScale;
  drawCoordinateSystem(drawer, {
    // Geometry points are drawn in local section coordinates where [0, 0] is the section origo.
    origin: [0, 0],
    axisLength,
    horizontalAxisLabel: "y",
    verticalAxisLabel: "z",
    axisStyle: coordinate_axis_style,
    labelStyle: coordinate_axis_text_style,
  });

  // draw stirrup
  if (shouldDrawStirrup(project.data)) {
    drawStirrup(drawer, project.data.data, geom);
  }

  // draw longitudal reinf
  drawLongitudalReinforcement(drawer, project.data.data, exactBarMarks);

  //
  // view direction
  //
  const a_style = props.a_selected ? view_sel_style : view_style;
  const b_style = props.b_selected ? view_sel_style : view_style;

  const view_dist_for_meassure = showMeassure ? cs_view_dist : cs_view_dist_alt;

  // section/material name, placed between the lowest measurement line and the view direction indicator
  const sectionLabel = section_material_label(project.data);
  if (sectionLabel !== null) {
    const label_top = geom.ver.max + (showMeassure ? cs_meassure_dist : 0) + (showFireIndicator ? FIRE_ICON_OFFSET : 0);
    const label_bottom = geom.ver.max + view_dist_for_meassure;
    drawer.drawText(
      [lerp(geom.hor.min, geom.hor.max, 0.5), lerp(label_top, label_bottom, 0.5)],
      sectionLabel,
      cs_section_label_style,
      { horiAlign: "center", vertAlign: "middle", id: "section-label" },
    );
  }

  // view direction A (horizontal to up)
  drawViewDirection_up_left(drawer, a_style, {
    hor: geom.hor.min - cs_view_dist_alt,
    ver: lerp(geom.ver.min, geom.ver.max, 0.5),
    onClicked: props.select_a,
  });
  drawViewDirection_up_right(drawer, a_style, {
    hor: geom.hor.max + view_dist_for_meassure,
    ver: lerp(geom.ver.min, geom.ver.max, 0.5),
    onClicked: props.select_a,
  });
  // view direction B (vertical to left)
  // top
  drawViewDirection_left_top(drawer, b_style, {
    hor: lerp(geom.hor.min, geom.hor.max, 0.5),
    ver: geom.ver.min - cs_view_dist_alt,
    onClicked: props.select_b,
  });
  // bottom
  drawViewDirection_left_bottom(drawer, b_style, {
    hor: lerp(geom.hor.min, geom.hor.max, 0.5),
    ver: geom.ver.max + view_dist_for_meassure,
    onClicked: props.select_b,
  });

  const items = drawer.toDrawables();
  return <Svg<ArrowId> items={items} markers={ColumnMarkers()} isDev={isDev} autoZoom={autoZoom} />;
};
